/* eslint-disable sonarjs/cognitive-complexity */
import { marked, type RendererObject } from 'marked';
import {
  type TextMarkdownEvent,
  type ItemValue,
  type SurveyModel,
} from 'survey-core';

import { type Brand } from '@/consts/brands';
import { FunnelService } from '@/services/funnelService/client';
import { type Visit } from '@/services/visitsService/types';
import { post } from '@/utils/http/client';

export const saveResponses = (
  survey: SurveyModel,
  provider: string,
  surveyId: number,
  brand: Brand,
  visit: Visit | undefined = undefined,
) => {
  const responses = [];
  const visitResponses = [];
  for (const key in survey.data) {
    const question = survey.getQuestionByName(key);
    if (question) {
      const shouldSkipPhotoUploadQuestions = !!visit;
      if (
        question.getType() === 'photo-upload' &&
        shouldSkipPhotoUploadQuestions
      ) {
        continue;
      }

      if (!question.answeredAt) {
        question.answeredAt = new Date().toISOString();
      }

      const answer = survey.getValue(key);
      responses.push({
        question: key,
        answer: answer,
        text: Array.isArray(answer)
          ? answer.map(
              (value) =>
                question.choices?.find(
                  (choice: ItemValue) => choice.value === value,
                )?.text,
            )
          : question.choices?.find(
              (choice: ItemValue) => choice.value === answer,
            )?.text,
        semanticMeaning: question.getPropertyValue('semanticMeaning'),
        answeredAt: question.answeredAt,
      });

      visitResponses.push({
        semantic_meanings: question.getPropertyValue('semanticMeaning'),
        question_title: question.title,
        question_description: question.description,
        options: question.choices?.map((choice: ItemValue) => ({
          value: choice.value,
          text: choice.text,
        })),
        response: answer,
      });
    } else {
      console.error('Question not found', key);
    }
  }

  if (visit && visitResponses.length > 0) {
    return post(`/api/visits/${visit.uuid}/responses`, {
      responses: visitResponses,
      context: `${survey.group}-v${survey.version}`,
    });
  } else {
    return FunnelService.saveSurvey(brand, provider, surveyId, {
      completedAt: new Date().toISOString(),
      responses,
    });
  }
};

const renderer = {
  heading({ tokens, depth }) {
    return `<h${depth} class="cur-text-h2 cur-font-heading cur-text-primary cur-leading-7 cur-mt-3">${this.parser.parseInline(tokens)}</h${depth}>`;
  },
  paragraph({ tokens }) {
    return `<p class="cur-mt-3 cur-font-body cur-text-body cur-tracking-body cur-leading-6 cur-text-primary cur-whitespace-pre-line">${this.parser.parseInline(tokens)}</p>`;
  },
  listitem({ tokens }) {
    return `<li class="cur-text-body cur-tracking-body cur-leading-6 cur-text-primary">${this.parser.parseInline(tokens)}</li>`;
  },
  list({ items }) {
    const content = items.map((item) => this.listitem(item)).join('');
    return `<ul class="cur-list-disc cur-list-inside cur-mt-3 cur-ml-3 cur-space-y-2 cur-text-body cur-tracking-body cur-leading-6 cur-text-primary">${content}</ul>`;
  },
} satisfies RendererObject;

marked.use({ renderer });

export const handleMarkdown = (
  survey: SurveyModel,
  options: TextMarkdownEvent,
) => {
  if (options.name === 'title') {
    options.text = `# ${options.text}`;
  }

  const html = marked(options.text) as string;

  options.html = html;
};
